import React, {useEffect, useRef} from 'react';
import {useThemeProvider} from '../../../../../mosaic/utils/ThemeContext.jsx';
import {chartColors} from '../../../../../mosaic/charts/ChartjsConfig.jsx';
import {
    CategoryScale,
    Chart,
    Filler,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-moment';
import {useTranslation} from "react-i18next";


Chart.register(LineController, LineElement, Filler, PointElement, LinearScale, TimeScale, Tooltip, CategoryScale);

export default function DailyOrderPerformanceChart({data, width, height}) {

    let {t} = useTranslation();

    const canvas = useRef(null);
    const chart = useRef(null);
    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {gridColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor} = chartColors;

    useEffect(() => {
        if (data === null || canvas.current === null) {
            return undefined;
        }

        const ctx = canvas.current;
        const newChart = new Chart(ctx, {
            type: 'line',
            data: data,
            options: {
                animation: false,
                layout: {
                    padding: {
                        top: 16,
                        bottom: 16,
                        left: 20,
                        right: 20,
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        border: {
                            display: false,
                        },
                        grid: {
                            drawTicks: false,
                            color: darkMode ? gridColor.dark : gridColor.light,
                        },
                        ticks: {
                            maxTicksLimit: 2,
                            display: false,
                        },
                    },
                    x: {
                        display: false,
                    },
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => false, // Disable tooltip title
                            label: (context) => [
                                renderDate(new Date(Date.parse(context.label))),
                                renderTime(new Date(Date.parse(context.label)), true),
                                t('orders.x-orders', {count: context.raw})
                            ],
                        },
                        bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
                        backgroundColor: darkMode ? 'rgba(15,25,40,0.5)' : 'rgba(255,255,255,0.5)',
                        borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
                        opacity: 0.5,
                    },
                    legend: {
                        display: false,
                    },
                },
                interaction: {
                    intersect: false,
                    mode: 'nearest',
                },
                maintainAspectRatio: false,
            },
        });

        chart.current = newChart;

        return () => {
            newChart.destroy();
            if (chart.current === newChart) {
                chart.current = null;
            }
        };
    }, [data]);

    useEffect(() => {
        if (chart.current === null) {
            return;
        }

        if (darkMode) {
            chart.current.options.scales.y.grid.color = gridColor.dark;
            chart.current.options.plugins.tooltip.bodyColor = tooltipBodyColor.dark;
            chart.current.options.plugins.tooltip.backgroundColor = tooltipBgColor.dark;
            chart.current.options.plugins.tooltip.borderColor = tooltipBorderColor.dark;
        } else {
            chart.current.options.scales.y.grid.color = gridColor.light;
            chart.current.options.plugins.tooltip.bodyColor = tooltipBodyColor.light;
            chart.current.options.plugins.tooltip.backgroundColor = tooltipBgColor.light;
            chart.current.options.plugins.tooltip.borderColor = tooltipBorderColor.light;
        }

        chart.current.update('none');
    }, [currentTheme]);

    return (<canvas ref={canvas} width={width} height={height}></canvas>);
}
